import type {
  IThemeManager,
  SystemColorScheme,
  ThemeChangeListener,
  ThemeId,
  ThemePreference,
} from "@project/design-system-core";

import {
  DEFAULT_THEME_CUSTOMIZATIONS,
  DEFAULT_THEME_PREFERENCE,
  THEME_STORAGE_KEY,
  resolveTheme,
} from "@project/design-system-core";

import {generateThemeCSS, injectThemeStyles} from "./css-injection";
import {applyTheme} from "./theme";

function getSystemScheme(): SystemColorScheme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function parseStoredPreference(raw: string | null): ThemePreference {
  if (raw === "system" || raw === "light" || raw === "dark") return raw;

  return DEFAULT_THEME_PREFERENCE;
}

/**
 * Singleton theme manager for the web platform.
 *
 * Implements IThemeManager using:
 *  - localStorage for persistence (synchronous — no FOUC on web)
 *  - window.matchMedia for system color scheme detection
 *  - applyTheme() for DOM attribute manipulation
 *
 * Native will implement the same IThemeManager contract using
 * AsyncStorage + Appearance API + Uniwind.setTheme().
 */
export class WebThemeManager implements IThemeManager {
  private static _instance: WebThemeManager | null = null;

  private _preference: ThemePreference = DEFAULT_THEME_PREFERENCE;
  private _activeTheme: ThemeId = "light";
  private _listeners: Set<ThemeChangeListener> = new Set();
  private _mediaQuery: MediaQueryList | null = null;
  private _initialized = false;

  private constructor() {}

  static getInstance(): WebThemeManager {
    if (!WebThemeManager._instance) {
      WebThemeManager._instance = new WebThemeManager();
    }

    return WebThemeManager._instance;
  }

  /**
   * Reads from localStorage + system preference, resolves and applies the theme.
   * localStorage is synchronous — theme is applied before React mounts (no FOUC).
   * Returns Promise.resolve() for IThemeManager contract compatibility with native.
   */
  init(): Promise<void> {
    if (this._initialized) return Promise.resolve();
    this._initialized = true;

    const stored = localStorage.getItem(THEME_STORAGE_KEY);

    this._preference = parseStoredPreference(stored);
    this._activeTheme = resolveTheme(this._preference, getSystemScheme());

    // Inject brand token overrides before applying the theme to avoid FOUC
    injectThemeStyles(generateThemeCSS(DEFAULT_THEME_CUSTOMIZATIONS));
    applyTheme(this._activeTheme);

    // Watch for OS color scheme changes and re-apply when preference is 'system'
    this._mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    this._mediaQuery.addEventListener("change", this._onSystemSchemeChange);

    return Promise.resolve();
  }

  getPreference(): ThemePreference {
    return this._preference;
  }

  getActiveTheme(): ThemeId {
    return this._activeTheme;
  }

  async setPreference(preference: ThemePreference): Promise<void> {
    this._preference = preference;
    localStorage.setItem(THEME_STORAGE_KEY, preference);

    const resolved = resolveTheme(preference, getSystemScheme());

    this._activeTheme = resolved;
    applyTheme(resolved);
    this._notify();
  }

  subscribe(listener: ThemeChangeListener): () => void {
    this._listeners.add(listener);

    return () => this._listeners.delete(listener);
  }

  private _notify(): void {
    for (const listener of this._listeners) {
      listener(this._activeTheme, this._preference);
    }
  }

  private _onSystemSchemeChange = (): void => {
    // Only re-resolve if the user hasn't pinned a specific theme
    if (this._preference !== "system") return;
    const resolved = resolveTheme("system", getSystemScheme());

    if (resolved === this._activeTheme) return;
    this._activeTheme = resolved;
    applyTheme(resolved);
    this._notify();
  };
}
